import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './DeadlineTimeline.module.css';

const DeadlineTimeline = ({ 
  deadlines = [], 
  title = "Application Deadlines",
  className = "",
  colorTheme = {
    early: '#6366F1',      // Indigo (cooler than orange)
    regular: '#3B82F6',    // Blue
    financialAid: '#06B6D4' // Cyan (cooler than green)
  }
}) => {
  const [hoveredMilestone, setHoveredMilestone] = useState(null);
  const [focusedMilestone, setFocusedMilestone] = useState(null);
  const [isHorizontalScroll, setIsHorizontalScroll] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const timelineRef = useRef(null);

  // Process and sort deadlines by date
  const processedDeadlines = useMemo(() => {
    if (!deadlines || deadlines.length === 0) return [];

    const validDeadlines = deadlines
      .filter(deadline => deadline.date && deadline.label)
      .map(deadline => ({
        ...deadline,
        dateObj: new Date(deadline.date),
        id: `${deadline.label}-${deadline.date}-${deadline.type}`
      }))
      .filter(deadline => !isNaN(deadline.dateObj))
      .sort((a, b) => a.dateObj - b.dateObj);

    return validDeadlines;
  }, [deadlines]);

  // Calculate timeline bounds and positions
  const timelineData = useMemo(() => {
    if (processedDeadlines.length === 0) return null;

    const earliestDate = processedDeadlines[0].dateObj;
    const latestDate = processedDeadlines[processedDeadlines.length - 1].dateObj;
    const totalDuration = latestDate - earliestDate;
    const today = new Date();

    // Add padding to the timeline (10% on each side)
    const padding = totalDuration * 0.1;
    const startDate = new Date(earliestDate.getTime() - padding);
    const endDate = new Date(latestDate.getTime() + padding);
    const paddedDuration = endDate - startDate;

    // Calculate positions
    const milestonesWithPositions = processedDeadlines.map(deadline => {
      const position = ((deadline.dateObj - startDate) / paddedDuration) * 100;
      return {
        ...deadline,
        position: Math.max(0, Math.min(100, position))
      };
    });

    // Calculate today's position
    const todayPosition = today >= startDate && today <= endDate 
      ? ((today - startDate) / paddedDuration) * 100 
      : null;

    return {
      milestones: milestonesWithPositions,
      startDate,
      endDate,
      todayPosition: todayPosition ? Math.max(0, Math.min(100, todayPosition)) : null
    };
  }, [processedDeadlines]);

  // Check if horizontal scrolling is needed
  useEffect(() => {
    const checkScrollNeed = () => {
      if (timelineRef.current) {
        const containerWidth = timelineRef.current.offsetWidth;
        const minWidthNeeded = processedDeadlines.length * 80; // Minimum space per milestone
        setIsHorizontalScroll(containerWidth < minWidthNeeded);
      }
    };

    checkScrollNeed();
    window.addEventListener('resize', checkScrollNeed);
    return () => window.removeEventListener('resize', checkScrollNeed);
  }, [processedDeadlines.length]);

  // Format date for display
  const formatDate = (dateObj) => {
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get type display name
  const getTypeDisplayName = (type) => {
    const typeNames = {
      early: 'Early Action/Decision',
      regular: 'Regular Decision',
      financialAid: 'Financial Aid'
    };
    return typeNames[type] || type;
  };

  // Handle milestone interaction
  const handleMilestoneInteraction = (milestone, isHover = false, event = null) => {
    if (isHover) {
      setHoveredMilestone(milestone);
      // Calculate tooltip position based on milestone position
      if (event && timelineRef.current) {
        const timelineRect = timelineRef.current.getBoundingClientRect();
        const milestonePosition = milestone.position;
        
        // Calculate position within the timeline container
        const milestoneX = timelineRect.left + (milestonePosition / 100) * timelineRect.width;
        const milestoneY = timelineRect.top + timelineRect.height / 2;
        
        // Position tooltip above the milestone with some offset
        setTooltipPosition({
          x: milestoneX,
          y: milestoneY - 60 // 60px above the milestone
        });
      }
    } else {
      setFocusedMilestone(milestone);
      // For keyboard focus, use a more central position
      if (timelineRef.current) {
        const timelineRect = timelineRef.current.getBoundingClientRect();
        setTooltipPosition({
          x: timelineRect.left + timelineRect.width / 2,
          y: timelineRect.top - 40
        });
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const milestoneVariants = {
    hidden: { x: -50, opacity: 0, scale: 0.8 },
    visible: { 
      x: 0, 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: { 
      scale: 1.2,
      transition: { duration: 0.2 }
    }
  };

  const todayMarkerVariants = {
    hidden: { opacity: 0, scaleY: 0 },
    visible: { 
      opacity: 1, 
      scaleY: 1,
      transition: { 
        delay: 1,
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  if (!timelineData || processedDeadlines.length === 0) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.emptyState}>
          <h3>{title}</h3>
          <p>No deadlines available. Add some colleges to see their application deadlines!</p>
        </div>
      </div>
    );
  }

  const activeTooltip = hoveredMilestone || focusedMilestone;

  return (
    <motion.div 
      className={`${styles.container} ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h3 
        className={styles.title}
        variants={{
          hidden: { opacity: 0, y: -10 },
          visible: { opacity: 1, y: 0 }
        }}
      >
        {title}
      </motion.h3>

      <motion.div 
        className={styles.timelineWrapper}
        variants={{
          hidden: { opacity: 0, scale: 0.95 },
          visible: { opacity: 1, scale: 1 }
        }}
      >
        <div 
          ref={timelineRef}
          className={`${styles.timelineContainer} ${isHorizontalScroll ? styles.scrollable : ''}`}
        >
          {/* Timeline axis */}
          <div className={styles.timelineAxis}>
            {/* Today marker */}
            {timelineData.todayPosition !== null && (
              <motion.div 
                className={styles.todayMarker}
                style={{ left: `${timelineData.todayPosition}%` }}
                variants={todayMarkerVariants}
              >
                <div className={styles.todayLine} />
                <div className={styles.todayLabel}>Today</div>
              </motion.div>
            )}

            {/* Date labels */}
            <div className={styles.dateLabels}>
              <span className={styles.startDate}>
                {formatDate(timelineData.startDate)}
              </span>
              <span className={styles.endDate}>
                {formatDate(timelineData.endDate)}
              </span>
            </div>

            {/* Milestones */}
            {timelineData.milestones.map((milestone, index) => (
              <motion.div
                key={milestone.id}
                className={styles.milestone}
                style={{ 
                  left: `${milestone.position}%`,
                  '--milestone-color': colorTheme[milestone.type] || '#666666'
                }}
                variants={milestoneVariants}
                whileHover="hover"
                onMouseEnter={(e) => handleMilestoneInteraction(milestone, true, e)}
                onMouseLeave={() => setHoveredMilestone(null)}
                onFocus={(e) => handleMilestoneInteraction(milestone, false, e)}
                onBlur={() => setFocusedMilestone(null)}
                tabIndex={0}
                role="button"
                aria-label={`${milestone.label} - ${formatDate(milestone.dateObj)}`}
              >
                <div className={styles.milestoneDot} />
                <motion.div 
                  className={styles.milestonePulse}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <motion.div 
          className={styles.legend}
          variants={{
            hidden: { opacity: 0, y: 10 },
            visible: { opacity: 1, y: 0 }
          }}
          transition={{ delay: 0.8 }}
        >
          {Object.entries(colorTheme).map(([type, color]) => {
            const hasType = processedDeadlines.some(d => d.type === type);
            if (!hasType) return null;
            
            return (
              <div key={type} className={styles.legendItem}>
                <div 
                  className={styles.legendColor} 
                  style={{ backgroundColor: color }}
                />
                <span className={styles.legendText}>
                  {getTypeDisplayName(type)}
                </span>
              </div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Tooltip */}
      <AnimatePresence>
        {activeTooltip && (
          <motion.div
            className={styles.tooltip}
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            <div className={styles.tooltipHeader}>
              <div 
                className={styles.tooltipTypeIndicator}
                style={{ backgroundColor: colorTheme[activeTooltip.type] }}
              />
              <span className={styles.tooltipType}>
                {getTypeDisplayName(activeTooltip.type)}
              </span>
            </div>
            <div className={styles.tooltipContent}>
              <div className={styles.tooltipLabel}>{activeTooltip.label}</div>
              <div className={styles.tooltipDate}>
                {formatDate(activeTooltip.dateObj)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DeadlineTimeline; 